/* eslint-disable react/jsx-one-expression-per-line */
import React, { useRef, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useLang } from '~/hooks/useLang'
import ErrorFallback from '~/components/ErrorFallback'
import {
  Divider,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  Button,
  Collapse,
  Stack,
  Alert,
} from '@mui/material'
import { useHookstate } from '@hookstate/core'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import { deviceResponseProps } from '../../interfaces/ISearchstaff'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import SearchIcon from '@mui/icons-material/Search'
import ExpandLess from '@mui/icons-material/ExpandLess'
import dayjs from 'dayjs'
import { useMarkerState } from '~/features/Map/stores/MapState'
import { LatLng } from 'leaflet'
import { getDeviceInfo } from '../../services'

// eslint-disable-next-line max-lines-per-function
export default function SearchStaff() {
  const { translate } = useLang()
  const loading = useHookstate(false)
  const resetValeus = useHookstate(false)
  const resetDeviceField = useHookstate(false)
  const resetUserField = useHookstate(false)
  const deviceId = useHookstate<number | null>(null)
  const userId = useHookstate<number | null>(null)
  const [showValidationError, setShowValidationError] = React.useState(false)

  const [filtersOpen, setFiltersOpen] = React.useState(true)
  const [height, setHeight] = useState(335) 

  const gridRef = useRef<HTMLDivElement>(null)

  const { forceViewMap, markersState } = useMarkerState()
  const devicesInfoDefault = {
    id: 0,
  }
  const devicesInfo = useHookstate<deviceResponseProps>(devicesInfoDefault)
  const focusChange = useHookstate<boolean>(false)



  useEffect(() => {
    if (filtersOpen) {
      setHeight(335)
    } else {
      const timer = setTimeout(() => {
        setHeight(425)
      }, 300)
      
      return () => clearTimeout(timer)
    }
  }, [filtersOpen])

  const requestDevice = () => {
    if (deviceId.get() && userId.get()) {
      setShowValidationError(true)
      setTimeout(() => setShowValidationError(false), 5000)
      return
    }

    if (!deviceId.get() && !userId.get()) {
      return
    }

    setShowValidationError(false)
    loading.set(true)
    focusChange.set(false)
    getDeviceInfo({
      deviceId: deviceId.get(),
      userId: userId.get(),
    })
      .then(({ data }) => {
        devicesInfo.set(data)
      })
      .finally(() => {
        loading.set(false)
      })
  }

  const clearFilters = () => {
    deviceId.set(null)
    userId.set(null)
    devicesInfo.set(devicesInfoDefault)
    resetValeus.set(!resetValeus.get())
    resetDeviceField.set(!resetDeviceField.get())
    resetUserField.set(!resetUserField.get())
    setShowValidationError(false)
  }

  const handleDeviceChange = (device: number | null) => {
    if (device && userId.get()) {
      userId.set(null)
      resetUserField.set(!resetUserField.get())
    }
    deviceId.set(device)
    setShowValidationError(false)
  }

  const handleUserChange = (user: number | null) => {
    if (user && deviceId.get()) {
      deviceId.set(null)
      resetDeviceField.set(!resetDeviceField.get())
    }
    userId.set(user)
    setShowValidationError(false)
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Grid container width={'100%'}>
        <Grid item xs={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            pb={1}
          >
            <Typography
              variant="h6"
              component="h2"
              sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 400 }}
            >
              {translate('Filters')}
            </Typography>
            <IconButton
              onClick={() => setFiltersOpen((prev) => !prev)}
              size="small"
              sx={{ 
                ml: 1,
                transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: filtersOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <ExpandLess />
            </IconButton>
          </Stack>

          <Collapse 
            in={filtersOpen}
            timeout={{
              enter: 300,
              exit: 300
            }}
            easing={{
              enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
              exit: 'cubic-bezier(0.4, 0, 0.6, 1)'
            }}
          >
            {showValidationError && (
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Alert severity="warning">
                  {translate('You can only select one filter at a time')}
                </Alert>
              </Grid>
            )}

            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <FilterAutocompleteFetch
                  label={'device'}
                  urlFetch={'/device/find'}
                  size={'small'}
                  disabled={false}
                  dataRequest="device"
                  onFetchChange={handleDeviceChange}
                  resetValues={resetDeviceField.get()}
                  focusChange={focusChange}
                  resetValueNewProps
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FilterAutocompleteFetch
                  label={'User'}
                  urlFetch={'/user/get'}
                  size={'small'}
                  disabled={false}
                  onFetchChange={handleUserChange}
                  resetValues={resetUserField.get()}
                  focusChange={focusChange}
                  // model='user'
                  resetValueNewProps
                  valueId={userId.get() ? Number(userId.get()) : undefined}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="text"
                  onClick={clearFilters}
                  sx={{
                    height: '40px',
                    textTransform: 'none',
                    textDecorationLine: 'underline',
                    color: 'text.primary',
                    ':hover': {
                      bgcolor: 'transparent',
                      color: 'primary.main',
                    },
                  }}
                >
                  {translate('Clear filters')}
                </Button>
                <Tooltip title={translate('Search')} placement="bottom">
                  <IconButton
                    onClick={requestDevice}
                    disabled={!deviceId.get() && !userId.get()}
                    aria-label="search-staff"
                    title={translate('Search')}
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
            <Divider />
          </Collapse>
        </Grid>

        <Grid item xs={12}>
            {devicesInfo.get() && devicesInfo.get().name ? (
              <Grid
                key={devicesInfo.get()?.id}
                container
                display={'flex'}
                alignItems={'start'}
                justifyContent={'space-between'}
              >
                <Grid
                  ref={gridRef}
                  width={'100%'}
                  display={'flex'}
                  flexDirection={'row'}
                  height={height}
                  alignItems={'center'}
                  sx={{ 
                    overflowY: 'auto', 
                    transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <ListItem
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      height: '100%',
                      padding: 0,
                      width: '100%',
                    }}
                  >
                    <ListItemText
                      sx={{ width: '100%' }}
                      primary={
                        <Grid
                          width={'100%'}
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'center'}
                          justifyContent={'space-between'}
                        >
                          <Typography>
                            {`${translate('Name')}: ${devicesInfo.get()?.name}`}
                          </Typography>
                          <Tooltip
                            title={translate('Locate on the map')}
                            placement="bottom"
                            sx={{
                              '&:hover': {
                                background: 'none',
                              },
                            }}
                          >
                            <IconButton
                              aria-label="comment"
                              onClick={() => {
                                if (devicesInfo.get()?.id) {
                                  forceViewMap().zoom.set(17)
                                  forceViewMap().position.set(
                                    new LatLng(
                                      markersState()['device'][
                                        devicesInfo.get()?.id
                                      ].position.lat.get(),
                                      markersState()['device'][
                                        devicesInfo.get()?.id
                                      ].position.lng.get()
                                    )
                                  )
                                }
                              }}
                              sx={{
                                '&:hover': {
                                  background: 'none',
                                },
                              }}
                              disabled={
                                Boolean(
                                  !markersState()['device'].get()[
                                    devicesInfo.get()?.id
                                  ]?.position
                                ) || !devicesInfo.get()?.stationHasAccess
                              }
                            >
                              <TravelExploreIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      }
                      secondary={
                        <>
                          <Typography variant={'subtitle2'}>
                            {`${translate('Plate')}: ${
                              devicesInfo.get()?.vehicle !== null
                                ? devicesInfo.get()?.vehicle?.plate
                                : translate('Not informed')
                            }`}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {`${translate('Device Type')} : ${
                              devicesInfo.get()?.device_type?.name
                                ? devicesInfo.get()?.device_type?.name
                                : translate('Not informed')
                            }`}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {`${translate('Dispatch Group')} : ${
                              devicesInfo.get()?.dispatch_group?.name
                                ? devicesInfo.get()?.dispatch_group?.name
                                : translate('Not informed')
                            }`}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {`${translate('Task')}: ${
                              devicesInfo.get()?.device_log?.task &&
                              devicesInfo.get()?.device_log?.task !== ''
                                ? devicesInfo.get()?.device_log?.task
                                : translate('Not informed')
                            }`}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {`${translate('Status')}: ${
                              devicesInfo.get()?.status?.name !== null
                                ? devicesInfo.get()?.status?.name
                                : translate('Not informed')
                            }`}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {`${translate('Connected on')} : ${
                              devicesInfo.get()?.device_log?.date_connected !==
                              null
                                ? dayjs(
                                    devicesInfo.get()?.device_log
                                      ?.date_connected
                                  ).format('DD/MM/YYYY HH:mm:ss')
                                : translate('Not informed')
                            }`}
                          </Typography>
                          {devicesInfo.get()?.status?.acronym === 'DC' ? (
                            <Typography variant={'subtitle2'}>
                              {`${translate('Disconnected on')} : ${
                                devicesInfo.get()?.device_log
                                  ?.date_connected !== null
                                  ? dayjs(
                                      devicesInfo.get()?.device_log
                                        ?.date_disconnected
                                    ).format('DD/MM/YYYY HH:mm:ss')
                                  : translate('Not informed')
                              }`}
                            </Typography>
                          ) : (
                            ''
                          )}
                        </>
                      }
                    />
                    <ListItemText
                      primary={`${translate('Composition')}`}
                      secondary={devicesInfo
                        .get()
                        ?.device_log?.crew?.map((element) => {
                          return element != null ? (
                            <Typography key={devicesInfo.get()?.device_log?.id}>
                              {`${element.position.name} - ${element.user.representation_name} `}
                            </Typography>
                          ) : (
                            <Typography>{translate('Not informed')}</Typography>
                          )
                        })}
                    />
                  </ListItem>
                </Grid>
              </Grid>
            ) : (
              <Grid
                item
                xs={12}
                style={{
                  height: height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  boxSizing: 'border-box',
                  transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <Typography>{translate('No Staff')}</Typography>
              </Grid>
            )}
        </Grid>
      </Grid>
    </ErrorBoundary>
  )
}
